import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  User, 
  Building, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { fetchRecentActivity, deleteSession } from '../services/adminService';
import { AdminSession } from '../types/admin.types';
import { isTasOnlyMode, isRoiAnalysisMode, getReportUrl } from '../utils/calculatorPathUtils';

interface ActivityFeedProps {
  onSelectPerson: (email: string, name: string) => void;
  onSelectOrganization: (name: string) => void;
  onSelectSession: (session: AdminSession) => void;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ 
  onSelectPerson, 
  onSelectOrganization,
  onSelectSession 
}) => {
  const [activities, setActivities] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingSession, setDeletingSession] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    if (!refreshing) setLoading(true);
    try {
      const data = await fetchRecentActivity(100);
      setActivities(data);
    } catch (error) {
    }
    setLoading(false);
    setRefreshing(false);
  }, [refreshing]);

  useEffect(() => {
    loadActivities();
    // Refresh every 30 seconds
    const interval = setInterval(loadActivities, 30000);
    return () => clearInterval(interval);
  }, [loadActivities]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadActivities();
  };

  const handleDeleteSession = async (session: AdminSession) => {
    setDeletingSession(session.sessionId);
    setShowDeleteConfirm(null);
    
    try {
      const result = await deleteSession(session);
      if (result.success) {
        // Remove from local state
        setActivities(prev => prev.filter(a => a.sessionId !== session.sessionId));
      } else {
        alert('Failed to delete session: ' + result.errors.join(', '));
      }
    } catch (error: any) {
      alert('Error deleting session: ' + error.message);
    } finally {
      setDeletingSession(null);
    }
  };

  const formatTime = (timestamp: any): string => {
    if (!timestamp) return 'Unknown time';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const getStatusIcon = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      case 'in_progress':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return 'Report Generated';
      case 'complete':
        return 'Assessment Complete';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Unknown';
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-4 md:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <span className="text-xs sm:text-sm text-gray-500 ml-7 sm:ml-0">
              {activities.length} sessions in the last 30 days
            </span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {activities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">No recent activity found</p>
          </div>
        ) : (
          activities.map((session) => (
            <div
              key={session.sessionId}
              onClick={() => onSelectSession(session)}
              className="px-4 md:px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {getStatusIcon(session.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectPerson(session.userEmail, session.userName);
                        }}
                        className="font-medium text-gray-900 hover:text-sky-600 transition-colors flex items-center gap-1 text-sm sm:text-base"
                      >
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{session.userName || session.userEmail}</span>
                      </button>
                      <span className="text-gray-500 text-sm hidden sm:inline">assessed</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectOrganization(session.organizationName);
                        }}
                        className="font-medium text-gray-900 hover:text-sky-600 transition-colors flex items-center gap-1 text-sm sm:text-base"
                      >
                        <Building className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{session.organizationName}</span>
                      </button>
                    </div>
                    <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm text-gray-500">
                      <span>{formatTime(session.finalizedAt || session.completedAt || session.startedAt)}</span>
                      <span className="text-gray-300 hidden sm:inline">•</span>
                      <span>{getStatusLabel(session.status)}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                      {session.isRoosterAssessment && (
                        <span className="bg-purple-100 text-purple-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                          Rooster
                        </span>
                      )}
                      {isTasOnlyMode(session) && (
                        <span className="bg-blue-100 text-blue-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                          TAS
                        </span>
                      )}
                      {isRoiAnalysisMode(session) && (
                        <span className="bg-green-100 text-green-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                          ROI
                        </span>
                      )}
                      {session.leadSource && (
                        <span className="bg-indigo-100 text-indigo-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                          <span className="hidden sm:inline">Source: </span>{session.leadSource}
                        </span>
                      )}
                      {session.isEditSession && (
                        <span className="bg-amber-100 text-amber-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                          <span className="hidden sm:inline">Editing Session</span>
                          <span className="sm:hidden">Edit</span>
                        </span>
                      )}
                      {session.currentTAS && (
                        <span className="font-medium text-gray-500 text-[10px] sm:text-xs">
                          TAS: {Math.round(session.currentTAS)}
                        </span>
                      )}
                      {(session.year1Savings || session.steadyStateSavings) ? (
                        <span className="font-medium text-green-600 text-[10px] sm:text-xs">
                          <span className="hidden sm:inline">Y1: ${Math.round(session.year1Savings || 0).toLocaleString()} | SS: ${Math.round(session.steadyStateSavings || 0).toLocaleString()}</span>
                          <span className="sm:hidden">${Math.round((session.year1Savings || 0) / 1000)}K</span>
                        </span>
                      ) : session.totalAnnualSavings ? (
                        <span className="font-medium text-green-600 text-[10px] sm:text-xs">
                          <span className="hidden sm:inline">${Math.round(session.totalAnnualSavings).toLocaleString()} savings</span>
                          <span className="sm:hidden">${Math.round(session.totalAnnualSavings / 1000)}K</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:ml-4">
                  {session.status === 'finalized' && session.reportId && (
                    <a
                      href={`${getReportUrl(session)}?from=admin`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">View Report</span>
                      <span className="sm:hidden">Report</span>
                    </a>
                  )}
                  
                  {/* Delete button */}
                  {showDeleteConfirm === session.sessionId ? (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDeleteSession(session)}
                        disabled={deletingSession === session.sessionId}
                        className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50"
                      >
                        {deletingSession === session.sessionId ? 'Deleting...' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(session.sessionId);
                      }}
                      disabled={deletingSession !== null}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete session"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;